import React, { useState, useEffect } from 'react';
import { Redirect, Route } from 'react-router';
import { useLocation } from 'react-router-dom';
import hasToken from './services/authService';
import {
  refreshTokenForValidation,
  isNetworkOrServerError,
  isRefreshCooldownError,
} from './actions/tokenUtils';
import {
  isRouteAuthValidated,
  setRouteAuthValidated,
} from './services/routeAuthCache';
import BrandedRouteLoading from './components/RouteLoading/BrandedRouteLoading';

/** Cek apakah ada data user & token di localStorage (belum tentu valid) */
const hasAuthData = () => {
  const user = localStorage.getItem('user');
  const token = localStorage.getItem('token');
  return !!(user && token);
};

/**
 * Route terlindungi untuk /admin/*.
 * Hanya user yang sudah login yang bisa akses.
 * Redirect ke /beranda jika tidak terautentikasi.
 */
export const AdminRoute = ({ currentUser, dispatch, component, ...rest }) => {
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(
    () => hasAuthData() && !isRouteAuthValidated()
  );
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    hasAuthData() && isRouteAuthValidated() ? true : false
  );

  useEffect(() => {
    if (!hasAuthData()) {
      setIsAuthenticated(false);
      setIsValidating(false);
      return;
    }

    if (isRouteAuthValidated()) {
      setIsAuthenticated(true);
      setIsValidating(false);
      refreshTokenForValidation()
        .then((token) => {
          if (!token) {
            setRouteAuthValidated(false);
            setIsAuthenticated(false);
          }
        })
        .catch((err) => {
          if (!isNetworkOrServerError(err) && !isRefreshCooldownError(err)) {
            setRouteAuthValidated(false);
            setIsAuthenticated(false);
          }
        });
      return;
    }

    refreshTokenForValidation()
      .then((token) => {
        const ok = !!token;
        setIsAuthenticated(ok);
        if (ok) setRouteAuthValidated(true);
      })
      .catch((err) => {
        if (isNetworkOrServerError(err) || isRefreshCooldownError(err)) {
          setIsAuthenticated(true);
          setRouteAuthValidated(true);
        } else {
          setIsAuthenticated(false);
        }
      })
      .finally(() => setIsValidating(false));
  }, []);

  if (!hasAuthData()) {
    return (
      <Redirect
        to={{
          pathname: '/beranda',
          state: { from: location },
        }}
      />
    );
  }

  if (isValidating) {
    return <BrandedRouteLoading variant='gate' />;
  }

  if (!isAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: '/beranda',
          state: { from: location },
        }}
      />
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => React.createElement(component, props)}
    />
  );
};

/**
 * Route terlindungi untuk /template/*.
 * Hanya user yang sudah login yang bisa akses.
 * Redirect ke /beranda jika tidak terautentikasi.
 */
export const UserRoute = ({ dispatch, component, currentUser, ...rest }) => {
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(
    () => hasAuthData() && !isRouteAuthValidated()
  );
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    hasAuthData() && isRouteAuthValidated() ? true : false
  );

  useEffect(() => {
    if (!hasAuthData()) {
      setIsAuthenticated(false);
      setIsValidating(false);
      return;
    }

    if (isRouteAuthValidated()) {
      setIsAuthenticated(true);
      setIsValidating(false);
      refreshTokenForValidation()
        .then((token) => {
          if (!token) {
            setRouteAuthValidated(false);
            setIsAuthenticated(false);
          }
        })
        .catch((err) => {
          if (!isNetworkOrServerError(err) && !isRefreshCooldownError(err)) {
            setRouteAuthValidated(false);
            setIsAuthenticated(false);
          }
        });
      return;
    }

    refreshTokenForValidation()
      .then((token) => {
        const ok = !!token;
        setIsAuthenticated(ok);
        if (ok) setRouteAuthValidated(true);
      })
      .catch((err) => {
        if (isNetworkOrServerError(err) || isRefreshCooldownError(err)) {
          setIsAuthenticated(true);
          setRouteAuthValidated(true);
        } else {
          setIsAuthenticated(false);
        }
      })
      .finally(() => setIsValidating(false));
  }, []);

  if (!hasAuthData()) {
    return (
      <Redirect
        to={{
          pathname: '/beranda',
          state: { from: location },
        }}
      />
    );
  }

  if (isValidating) {
    return <BrandedRouteLoading variant='gate' />;
  }

  if (!isAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: '/beranda',
          state: { from: location },
        }}
      />
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => React.createElement(component, props)}
    />
  );
};

/**
 * Route publik: Login & Register.
 * Jika user sudah login (punya token valid), redirect ke halaman tujuan.
 */
export const AuthRoute = ({ dispatch, component, ...rest }) => {
  const location = useLocation();
  const from = location.state?.from
    ? location.state.from
    : { pathname: '/template/dashboard' };

  if (hasToken()) {
    const targetPath = typeof from === 'object' ? from.pathname : from;
    const safePath =
      targetPath && !targetPath.startsWith('/beranda') && !targetPath.startsWith('/register')
        ? targetPath
        : '/template/dashboard';
    return <Redirect to={safePath} />;
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        React.createElement(component, { ...props, location, from })
      }
    />
  );
};
